'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isResourceDropdownOpen, setIsResourceDropdownOpen] = useState(false);
  const [isMobileResourceOpen, setIsMobileResourceOpen] = useState(false);

  const navigationItems = [
    { label: 'Home', href: '/' },
    { label: 'Revenue Growth', href: '/revenue-growth-engine' },
    { label: 'Marketing Automation', href: '/marketing-automation' },
    { label: 'Customer Support', href: '/customer-support-ai' },
    { label: 'How It Works', href: '/how-it-works' },
  ];

  const resourceItems = [
    { label: 'FAQ', href: '/faq', icon: 'QuestionMarkCircleIcon' },
    { label: 'Tools & Assessments', href: '/tools-assessments', icon: 'WrenchScrewdriverIcon' },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setIsMobileResourceOpen(false);
  };

  const toggleMobileResource = () => {
    setIsMobileResourceOpen(!isMobileResourceOpen);
  };

  return (
    <header className={`bg-card shadow-md fixed top-0 left-0 right-0 z-50 ${className}`}>
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-4 lg:px-8">
          {/* Logo */}
          <Link href="/" className="flex items-center hover:opacity-80 transition-opacity">
            <Image
              src="/assets/images/rlx-banner-transparent-blk-small-1768239610518.png"
              alt="RedLetterX AI Logo"
              width={150}
              height={40}
              className="h-8 w-auto"
              priority
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="px-4 py-2 text-sm font-sans font-semibold text-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-300"
              >
                {item.label}
              </Link>
            ))}
            
            {/* Resource Center Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsResourceDropdownOpen(true)}
              onMouseLeave={() => setIsResourceDropdownOpen(false)}
            >
              <button
                className="px-4 py-2 text-sm font-sans font-semibold text-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-300 flex items-center"
              >
                Resource Center
                <Icon 
                  name="ChevronDownIcon" 
                  size={16} 
                  className={`ml-1 transition-transform duration-300 ${isResourceDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
              
              {isResourceDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-card border border-border rounded-lg shadow-elevated py-2 animate-slide-in">
                  {resourceItems.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className="flex items-center px-4 py-3 text-sm font-sans font-semibold text-foreground hover:text-primary hover:bg-muted transition-all duration-300"
                    >
                      <Icon name={item.icon} size={20} className="mr-3" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              href="/contact"
              className="px-6 py-2.5 bg-accent text-accent-foreground font-heading font-semibold rounded-lg hover:bg-accent/90 hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-0.5"
            >
              Schedule Consultation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 text-foreground hover:text-primary hover:bg-muted rounded-md transition-colors"
            aria-label="Toggle mobile menu"
            suppressHydrationWarning
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              variant="outline"
            />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-card border-t border-border animate-slide-in" suppressHydrationWarning>
            <nav className="px-4 py-4 space-y-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="block px-4 py-3 text-base font-sans font-semibold text-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-300"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              
              {/* Mobile Resource Center */}
              <div>
                <button
                  onClick={toggleMobileResource}
                  className="w-full flex items-center justify-between px-4 py-3 text-base font-sans font-semibold text-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-300"
                  suppressHydrationWarning
                >
                  Resource Center
                  <Icon 
                    name="ChevronDownIcon" 
                    size={20} 
                    className={`transition-transform duration-300 ${isMobileResourceOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                
                {isMobileResourceOpen && (
                  <div className="ml-4 mt-2 space-y-2" suppressHydrationWarning>
                    {resourceItems.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        className="flex items-center px-4 py-3 text-base font-sans font-semibold text-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-300"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Icon name={item.icon} size={20} className="mr-3" />
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              
              <Link
                href="/contact"
                className="block w-full px-4 py-3 mt-4 bg-accent text-accent-foreground text-center font-heading font-semibold rounded-lg hover:bg-accent/90 transition-all duration-300"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Schedule Consultation
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;